import { useEffect, useState } from 'react'
import { FaChevronCircleDown as Chevron, FaChevronCircleUp, FaSave } from 'react-icons/fa'
import { MdOutlineCancel } from 'react-icons/md'
import { useShallow } from 'zustand/react/shallow'

import { SliceHealthStatus, SliceStatusError } from '@ui/__generated__/graphql'
import { Button, SliceStatusToggles, TabSelector } from '@ui/components/core'
import { Spinner } from '@ui/components/loaders'
import { useNodeAndEdgeStore } from '@ui/stores'
import dayjs from 'dayjs'

interface SliceStatusByDatatype {
  logsStatus: SliceHealthStatus
  spansStatus: SliceHealthStatus
  metricsStatus: SliceHealthStatus
}

interface TopBarProps {
  setSaveTabOpen: (value: boolean) => void
  setParams: () => void
  cancelTransform: (allowCancel?: boolean) => void
  loading: boolean
  nodeStatus: SliceStatusByDatatype
  nodeEnabled: { logsEnabled: boolean; spansEnabled: boolean; metricsEnabled: boolean }
  nodeErrors: SliceStatusError[]
}

export function TopBar({
  setSaveTabOpen,
  setParams,
  cancelTransform,
  loading,
  nodeStatus,
  nodeEnabled,
  nodeErrors,
}: TopBarProps) {
  const [showBanner, setShowBanner] = useState(false)

  const { currentNodeId, setNodes } = useNodeAndEdgeStore(
    useShallow((state) => ({
      currentNodeId: state.currentNodeId,
      setNodes: state.setNodes,
    }))
  )

  useEffect(() => {
    setShowBanner(
      nodeStatus.logsStatus === SliceHealthStatus.Failed ||
        nodeStatus.spansStatus === SliceHealthStatus.Failed ||
        nodeStatus.metricsStatus === SliceHealthStatus.Failed
    )
  }, [currentNodeId, nodeStatus, setNodes, nodeEnabled])

  return (
    <div className="flex w-full flex-col">
      {showBanner && <ErrorBanner sliceStatus={nodeStatus} errors={nodeErrors} />}

      <div className={'flex w-full flex-row items-center justify-between'}>
        <TabSelector setParams={setParams} className={'ml-5 justify-center gap-x-8'} />

        <div className="flex flex-row">
          {loading && (
            <div className="mr-2 pt-3.5">
              <Spinner />
            </div>
          )}
          <span className="flex flex-row">
            <Button onClick={() => setSaveTabOpen(true)} data-testid={'code-save-page-enter'}>
              <FaSave className="m-auto mr-2" /> Save
            </Button>
          </span>

          <span className="flex flex-row">
            <Button
              variant="secondary"
              onClick={() => {
                cancelTransform(true)
              }}
              data-testid={'code-save-page-exit'}>
              <MdOutlineCancel className="m-auto mr-1 " />
              <span className="mr-1">Cancel</span>
            </Button>
          </span>
        </div>

        <SliceStatusToggles />
      </div>
    </div>
  )
}

export const ErrorBanner = ({
  sliceStatus,
  errors,
}: {
  sliceStatus: SliceStatusByDatatype
  errors: SliceStatusError[]
}) => {
  const [showBannerDropdown, setShowBannerDropdown] = useState(false)

  const failingDatatypes = []
  if (sliceStatus.logsStatus === SliceHealthStatus.Failed) failingDatatypes.push('logs')
  if (sliceStatus.spansStatus === SliceHealthStatus.Failed) failingDatatypes.push('spans')
  if (sliceStatus.metricsStatus === SliceHealthStatus.Failed) failingDatatypes.push('metrics')

  const limitedErrors = errors.slice(0, 4)

  return (
    <div
      className="relative flex grow cursor-pointer flex-col rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700 hover:bg-red-200"
      role="alert"
      onClick={() => setShowBannerDropdown(!showBannerDropdown)}>
      <div className="flex w-full flex-row items-center justify-between">
        <span>Error: Data flow has been automatically disabled for {formatDataTypeMessage(failingDatatypes)}.</span>
        {!showBannerDropdown && <Chevron className="h-5 w-5" />}
        {showBannerDropdown && <FaChevronCircleUp className="h-5 w-5" />}
      </div>
      <div>
        <span className="text-sm">
          Please resolve any errors and re-enable the datatype{failingDatatypes.length > 1 ? 's' : ''} below.
        </span>
      </div>

      {showBannerDropdown && (
        <>
          <hr className="mt-1 h-0.5 bg-red-500" />
          <div className="mt-2.5 flex h-fit flex-col space-y-2 overflow-y-auto">
            {limitedErrors.map((error, index) => (
              <div key={index} className="flex flex-row items-center justify-start">
                <p className="text-sm font-medium">{dayjs(error.timestamp).format('L LT')}</p>
                &nbsp;|&nbsp;
                <p className="text-md">{error.message}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const formatDataTypeMessage = (items: string[]): string => {
  if (items.length === 0) {
    return ''
  } else if (items.length === 1) {
    return items[0]
  } else if (items.length === 2) {
    return items.join(' and ')
  } else {
    const lastItem = items.pop() // Removes the last element and stores it
    return `${items.join(', ')}, and ${lastItem}`
  }
}
